import { createSlice } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";

import { RootState, SinglePropertyState } from "@/types/store";
import { PropertyType } from "@/types/enums";
import {
  createIndividualCalendarBlock,
  deleteIndividualCalendarBlock,
  getIndividualPropertyCalendarBlocks,
  getIndividualPropertyCalendarBlocksYearOverview,
  updateIndividualCalendarBlock,
} from "@/api/calendarBlocks/thunks";
import { blockNoticesReformat } from "@/utils/reformatApiData";
import { fromDateISOString } from "@/utils/dateFormatters";
import { ReservationTypeMapping } from "@/api/reservationTypeMappings/types";

const initialState: SinglePropertyState = {
  propertyName: "",
  propertyType: PropertyType.SINGLE_FAMILY_HOME,
  monthBlockNotices: [],
  yearBlockNotices: {},
  propertyId: null,
};

export const singlePropertiesSlice = createSlice({
  name: "SinglePropertiesSlice",
  initialState,
  reducers: {
    setPropertyData: (
      state,
      { payload: { propertyName, propertyType, propertyId } }
    ) => {
      state.propertyName = propertyName;
      state.propertyId = propertyId;
      state.propertyType = propertyType;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        getIndividualPropertyCalendarBlocks.fulfilled,
        (state, { payload }) => {
          if (!state.propertyId) return;
          const { data, mappingsMap } = payload as { data: CalendarBlockBE[]; mappingsMap: Record<string, ReservationTypeMapping> };
          state.monthBlockNotices = blockNoticesReformat(data, mappingsMap);
        }
      )
      .addCase(
        getIndividualPropertyCalendarBlocksYearOverview.fulfilled,
        (state, { payload }) => {
          if (!state.propertyId) return;
          state.yearBlockNotices = payload;
        }
      )
      .addCase(
        deleteIndividualCalendarBlock.fulfilled,
        (state, { payload }) => {
          if (!state.propertyId) return;
          const { blockId } = payload;
          state.monthBlockNotices = state.monthBlockNotices.filter((notice) => {
            return notice.block.id !== blockId;
          });
        }
      )
      .addCase(
        createIndividualCalendarBlock.fulfilled,
        (state, { payload }) => {
          if (!state.propertyId) return;
          const { data, mappingsMap } = payload as { data: CalendarBlockBE; mappingsMap: Record<string, ReservationTypeMapping> };
          const formatted = blockNoticesReformat([data], mappingsMap);
          // blockNoticesReformat filters hidden blocks
          if (formatted.length > 0) {
            state.monthBlockNotices.push(formatted[0]);
          }

          const startDate = fromDateISOString(data.start);
          const monthName = startDate
            .toLocaleString("default", { month: "long" })
            .toUpperCase();

          const currentYearNotices = state.yearBlockNotices;
          state.yearBlockNotices = {
            ...currentYearNotices,
            [monthName]: (currentYearNotices[monthName] || 0) + 1,
          };
        }
      )
      .addCase(
        updateIndividualCalendarBlock.fulfilled,
        (state, { payload }) => {
          if (!state.propertyId) return;
          const { data, mappingsMap } = payload as { data: CalendarBlockBE; mappingsMap: Record<string, ReservationTypeMapping> };
          const formatted = blockNoticesReformat([data], mappingsMap);
          const blockNotices = state.monthBlockNotices;
          const blockIndex = blockNotices.findIndex(
            (notice) => notice.block.id === data.id
          );
          if (blockIndex !== -1) {
            if (formatted.length === 0) {
              // Block type changed to hidden, remove it
              blockNotices.splice(blockIndex, 1);
            } else {
              blockNotices[blockIndex] = formatted[0];
            }
          }
        }
      );
  },
});

export const useSinglePropertiesData = () => {
  const singleProperty = useSelector(
    (state: RootState) => state.SingleProperty
  );
  const {
    monthBlockNotices,
    yearBlockNotices,
    propertyName,
    propertyType,
    propertyId,
  } = singleProperty;
  return {
    monthBlockNotices,
    yearBlockNotices,
    propertyName,
    propertyType,
    propertyId,
  };
};

export const { setPropertyData } = singlePropertiesSlice.actions;

export default singlePropertiesSlice.reducer;
